// src/services/auth-service.ts
import { authApi, type UserMe } from "@/api/auth";
import { getStoredToken } from "@/api/api-fetch";

export interface User {
  id: number;
  email: string;
  isAdmin: boolean;
  full_name?: string | null;
}

export interface LoginCredentials {
  email: string;
  password: string;
  rememberMe?: boolean;
}

export interface SignupData {
  name: string;
  email: string;
  password: string;
}

function setStoredFields(rememberMe: boolean, token: string, isAdmin: boolean, fullName?: string | null) {
  const adminVal = String(!!isAdmin);
  const nameVal = fullName || "";

  if (rememberMe) {
    sessionStorage.removeItem("access_token");
    sessionStorage.removeItem("is_admin");
    sessionStorage.removeItem("full_name");

    localStorage.setItem("access_token", token);
    localStorage.setItem("is_admin", adminVal);
    localStorage.setItem("full_name", nameVal);
  } else {
    localStorage.removeItem("access_token");
    localStorage.removeItem("is_admin");
    localStorage.removeItem("full_name");

    sessionStorage.setItem("access_token", token);
    sessionStorage.setItem("is_admin", adminVal);
    sessionStorage.setItem("full_name", nameVal);
  }
}

function clearStoredFields() {
  localStorage.removeItem("access_token");
  localStorage.removeItem("is_admin");
  localStorage.removeItem("full_name");
  sessionStorage.removeItem("access_token");
  sessionStorage.removeItem("is_admin");
  sessionStorage.removeItem("full_name");
  // Drop the cached nav entitlements (see use-entitlements CACHE_KEY) so the
  // next user never inherits the previous user's lock icons on first paint.
  localStorage.removeItem("nav_feature_locks");
}

function hasToken() {
  return !!getStoredToken();
}

/** Synchronous read of the logged-in user's stored full name ("" if none). */
export function getStoredFullName(): string {
  if (typeof window === "undefined") return "";
  return localStorage.getItem("full_name") || sessionStorage.getItem("full_name") || "";
}

/** First name only, derived from the stored full name ("" if none). */
export function getStoredFirstName(): string {
  return getStoredFullName().trim().split(/\s+/)[0] || "";
}

function mapMeToUser(me: UserMe): User {
  return {
    id: me.id,
    email: me.username,
    isAdmin: me.is_admin,
    full_name: me.full_name ?? null,
  };
}

/**
 * Custom error thrown by loginUser/signupUser when the server returns an MFA
 * state this legacy code path can't handle (it has no UI to render the QR
 * code or accept TOTP codes). Callers should catch this and redirect the
 * user to the MFA-aware <LoginPage /> flow instead.
 */
export class MfaRequiredError extends Error {
  constructor(
    public mfaState: "setup_required" | "challenge",
    message?: string,
  ) {
    super(
      message ||
      (mfaState === "setup_required"
        ? "Two-factor authentication setup is required. Please sign in via the login page."
        : "Two-factor authentication is required. Please sign in via the login page."),
    );
    this.name = "MfaRequiredError";
  }
}

export async function loginUser(credentials: LoginCredentials): Promise<User> {
  const rememberMe = credentials.rememberMe ?? true;

  const res = await authApi.login({
    username: credentials.email.toLowerCase(),
    password: credentials.password,
    rememberMe,
  });

  // The new login response branches on mfa_state. Only the "none" branch
  // returns an access_token; the other two return short-lived setup_token
  // or challenge_token and require the user to complete an MFA flow first.
  if (res.mfa_state !== "none" || !res.access_token) {
    throw new MfaRequiredError(
      res.mfa_state === "setup_required" ? "setup_required" : "challenge",
    );
  }

  setStoredFields(rememberMe, res.access_token, !!res.is_admin, res.full_name ?? null);

  const me = await authApi.getMe();
  // keep storage aligned with /me
  setStoredFields(rememberMe, res.access_token, !!me.is_admin, me.full_name ?? null);
  return mapMeToUser(me);
}

export async function signupUser(data: SignupData): Promise<User> {
  await authApi.register({
    username: data.email.toLowerCase(),
    password: data.password,
    full_name: data.name,
  });

  const loginRes = await authApi.login({
    username: data.email.toLowerCase(),
    password: data.password,
    rememberMe: true,
  });

  // New users default to mfa_required=true on the backend, so the very
  // first login after signup almost always returns mfa_state="setup_required".
  // This legacy auto-login path can't drive the MFA setup UI — bail out
  // and let the caller redirect to the proper login page.
  if (loginRes.mfa_state !== "none" || !loginRes.access_token) {
    throw new MfaRequiredError(
      loginRes.mfa_state === "setup_required" ? "setup_required" : "challenge",
      "Account created. Please sign in to set up two-factor authentication.",
    );
  }

  setStoredFields(true, loginRes.access_token, !!loginRes.is_admin, loginRes.full_name ?? data.name);

  const me = await authApi.getMe();
  setStoredFields(true, loginRes.access_token, !!me.is_admin, me.full_name ?? data.name);
  return mapMeToUser(me);
}

export async function getCurrentUser(): Promise<User | null> {
  if (!hasToken()) return null;

  try {
    const me = await authApi.getMe();
    return mapMeToUser(me);
  } catch {
    clearStoredFields();
    return null;
  }
}

export async function logoutUser(): Promise<void> {
  clearStoredFields();
}